import { useState } from 'react';
import { toast } from 'react-toastify';
import { FaTimes } from 'react-icons/fa';
import TextEdit from '../../../molecules/TextEdit';
import appConfig from '../../../../config/app-config';
import tokenAwareFetcher from '../../../../utils/tokenAwareFetcher';
import Dialog from '../../../organisms/Dialog';
import { DealItem } from '../../../../types/Contact';

const COMMUNICATION_ENV = appConfig.apis.communicationApiEnv;
const BASE_URL = COMMUNICATION_ENV.address;
const API_KEY = COMMUNICATION_ENV.key;
const fetcher = tokenAwareFetcher(BASE_URL, API_KEY);

interface CallRegistryModalFormProps {
  contactId: string;
  availableDeals?: DealItem[];
  preSelectedDeals?: DealItem[];
  onClose: () => void;
  onSuccess: (newActivity: any) => void;
  onDealsChange?: (deals: DealItem[]) => void;
  interactionType?: 'CALL' | 'EMAIL' | 'WHATSAPP';
  lockReason?: string;
}

export default function CallRegistryModalForm({ contactId, availableDeals = [], preSelectedDeals = [], onClose, onSuccess, onDealsChange, interactionType = 'CALL', lockReason }: CallRegistryModalFormProps) {
  const now = new Date();
  const defaultDateTime = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}T${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  const maxDateTime = `${now.getFullYear() + 1}-12-31T23:59`;

  const [reason, setReason] = useState('');
  const [observation, setObservation] = useState('');
  const [callDateTime, setCallDateTime] = useState(defaultDateTime);
  const [reasonPrefix, setReasonPrefix] = useState<'VALIDACAO' | 'COBRANCA' | 'PERSONALIZADO'>(lockReason ? 'VALIDACAO' : 'VALIDACAO');
  const [isSending, setIsSending] = useState(false);
  const [dateError, setDateError] = useState(false);
  const [selectedDeals, setSelectedDeals] = useState<DealItem[]>(preSelectedDeals);
  const [dealSearch, setDealSearch] = useState('');

  const buildReason = () => {
    const prefix = reasonPrefix === 'VALIDACAO' ? 'Validação' : reasonPrefix === 'COBRANCA' ? 'Cobrança' : '';
    return prefix || reason;
  };

  const filteredDeals = availableDeals.filter(d =>
    !selectedDeals.some(s => s.id === d.id) &&
    (dealSearch === '' || d.operation.toLowerCase().includes(dealSearch.toLowerCase()) || d.number.toLowerCase().includes(dealSearch.toLowerCase()) || (d.assignor || '').toLowerCase().includes(dealSearch.toLowerCase()))
  );

  const handleSubmit = async (answered: boolean) => {
    if (isSending) return;

    const parsedDate = new Date(callDateTime);
    if (!callDateTime || isNaN(parsedDate.getTime()) || parsedDate.getFullYear() < 2000 || parsedDate.getFullYear() > new Date().getFullYear() + 1) {
      setDateError(true);
      toast.error('Data inválida. Verifique o ano informado.');
      return;
    }
    setDateError(false);

    setIsSending(true);
    try {
      const deals = selectedDeals.length > 0
        ? selectedDeals.map(d => ({ dealId: d.id, operation: d.operation, dealNumber: d.number }))
        : undefined;

      const response = await fetcher.post(`/contacts/${contactId}/interactions`, {
          title: buildReason(),
          occurredAt: callDateTime,
          interactionType,
          body: observation,
          answered: answered,
          deals,
        });

      if (response.ok) {
        const newActivity = await response.json();
        toast.success('Registro salvo com sucesso!');
        onSuccess(newActivity);
      } else {
        const error = await response.json().catch(() => ({}));
        toast.error(error.message || 'Erro ao registrar');
      }
    } catch (error) {
      toast.error('Erro ao registrar');
    } finally {
      setIsSending(false);
    }
  };

  const Spinner = () => (
    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>
  );

  return (
    <Dialog
      id="call-registry-modal"
      show={true}
      onClose={onClose}
      header={interactionType === 'EMAIL' ? 'Registrar Email' : interactionType === 'WHATSAPP' ? 'Registrar WhatsApp' : 'Registrar Ligação'}
      size="md"
      body={
        <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
          <div>
            <input type="datetime-local" value={callDateTime} onChange={(e) => { setCallDateTime(e.target.value); setDateError(false); }} min="2000-01-01T00:00" max={maxDateTime} className={`w-full px-3 py-2 border rounded-lg focus:ring-2 dark:bg-slate-700 dark:text-white ${dateError ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 dark:border-slate-600 focus:ring-blue-500'}`} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Motivo</label>
            {lockReason ? (
              <span className="px-3 py-1.5 text-xs bg-white dark:bg-slate-600 text-gray-900 dark:text-white shadow-sm font-medium rounded-md border border-gray-200 dark:border-slate-500">{lockReason}</span>
            ) : (
            <div className="flex bg-gray-100 dark:bg-slate-700 rounded-lg p-0.5 w-fit">
              <button type="button" onClick={() => setReasonPrefix('VALIDACAO')} className={`px-3 py-1.5 text-xs rounded-md transition-colors ${reasonPrefix === 'VALIDACAO' ? 'bg-white dark:bg-slate-600 text-gray-900 dark:text-white shadow-sm font-medium' : 'text-gray-500 dark:text-gray-400'}`}>Validação</button>
              <button type="button" onClick={() => setReasonPrefix('COBRANCA')} className={`px-3 py-1.5 text-xs rounded-md transition-colors ${reasonPrefix === 'COBRANCA' ? 'bg-white dark:bg-slate-600 text-gray-900 dark:text-white shadow-sm font-medium' : 'text-gray-500 dark:text-gray-400'}`}>Cobrança</button>
              <button type="button" onClick={() => setReasonPrefix('PERSONALIZADO')} className={`px-3 py-1.5 text-xs rounded-md transition-colors ${reasonPrefix === 'PERSONALIZADO' ? 'bg-white dark:bg-slate-600 text-gray-900 dark:text-white shadow-sm font-medium' : 'text-gray-500 dark:text-gray-400'}`}>Personalizado</button>
            </div>
            )}
          </div>
          {reasonPrefix === 'PERSONALIZADO' && (
            <TextEdit label="Motivo personalizado" value={reason} onChange={(e) => setReason(e.target.value)} />
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Observação</label>
            <textarea value={observation} onChange={(e) => setObservation(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-slate-700 dark:text-white resize-none" rows={3} placeholder="Digite uma observação sobre a ligação..." />
          </div>

          {/* Deals section */}
          {availableDeals.length > 0 && (
            <div>
              {selectedDeals.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {selectedDeals.map(deal => (
                    <span key={deal.id} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full text-xs">
                      {deal.operation} • Nº {deal.number}
                      <button onClick={() => { const next = selectedDeals.filter(d => d.id !== deal.id); setSelectedDeals(next); onDealsChange?.(next); }} className="hover:text-blue-600 dark:hover:text-blue-300"><FaTimes size={10} /></button>
                    </span>
                  ))}
                </div>
              )}
              <div className="relative">
                <TextEdit
                  label="Duplicatas vinculadas"
                  value={dealSearch}
                  onChange={(e) => setDealSearch(e.target.value)}
                  placeholder=""
                />
                {dealSearch && filteredDeals.length > 0 && (
                  <div className="absolute left-0 right-0 top-full z-10 border border-gray-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 shadow-lg max-h-32 overflow-y-auto">
                    {filteredDeals.map(deal => (
                      <button key={deal.id} onClick={() => { const next = [...selectedDeals, deal]; setSelectedDeals(next); setDealSearch(''); onDealsChange?.(next); }} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-slate-700 border-b border-gray-100 dark:border-slate-700 last:border-0">
                        <span className="font-medium">{deal.operation}</span> • Nº {deal.number} {deal.assignor && <span className="text-gray-500 dark:text-gray-400">— {deal.assignor}</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      }
      footer={
        <div className="flex gap-3 w-full">
          {interactionType === 'CALL' ? (
            <>
              <button onClick={() => handleSubmit(false)} disabled={isSending} className="flex-1 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg text-sm disabled:opacity-50 flex items-center justify-center gap-2">
                {isSending && <Spinner />} Não atendido
              </button>
              <button onClick={() => handleSubmit(true)} disabled={isSending} className="flex-1 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm disabled:opacity-50 flex items-center justify-center gap-2">
                {isSending && <Spinner />} Atendido
              </button>
            </>
          ) : (
            <button onClick={() => handleSubmit(true)} disabled={isSending} className="flex-1 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm disabled:opacity-50 flex items-center justify-center gap-2">
              {isSending && <Spinner />} Registrar
            </button>
          )}
        </div>
      }
    />
  );
}
